/* ----------------------------------------------------------------------------
 * Support (tickets) — pure view/model layer (no network, no import.meta).
 *
 * DEPENDENCY-FREE so it unit-tests under `node --test --experimental-strip-types`
 * exactly like data/connections-model.ts and data/voicePace.ts (the repo's
 * no-jsdom convention: test the pure model the thin React view renders). The
 * fetch layer lives beside it in data/support.ts (which imports lib/api).
 *
 * Contract source of truth:
 *   bookedcentral-api/modules/support/Http/SupportRoutes.php   (staff CRUD)
 *   bookedcentral-api/modules/support/Services/SupportService.php (status machine)
 *   docs/verify/support/ddl-proposal-2026-07-25.md              (field shapes)
 *
 * SCAFFOLD: the module ships DARK behind an owner-gated DDL, so every read can
 * legitimately return 503 { code: 'support_unprovisioned' }. The UI must render
 * that as a calm "not turned on yet" state, NOT an error — isUnprovisioned() is
 * the single detector the pages use.
 * -------------------------------------------------------------------------- */

// ---- Endpoints (the ONLY support endpoints this surface is authorised to use) ----
export const SUPPORT_ENDPOINTS = {
  /** GET (list) + POST (create) tickets for the active workspace. */
  tickets: '/support/tickets',
  /** GET one ticket + its full staff thread. */
  ticket: (id: number | string): string => `/support/tickets/${id}`,
  /** POST a staff reply / internal note to a ticket. */
  messages: (id: number | string): string => `/support/tickets/${id}/messages`,
  /** POST an explicit status transition. */
  status: (id: number | string): string => `/support/tickets/${id}/status`,
} as const;

// ---- Status machine (mirror of SupportService) ------------------------------
export type TicketStatus =
  | 'open'
  | 'pending_customer'
  | 'pending_staff'
  | 'resolved'
  | 'closed';

export const TICKET_STATUSES: readonly TicketStatus[] = [
  'open',
  'pending_customer',
  'pending_staff',
  'resolved',
  'closed',
] as const;

/** Human labels for each status (what the inbox chip reads). */
export const STATUS_LABEL: Record<TicketStatus, string> = {
  open: 'Open',
  pending_customer: 'Awaiting customer',
  pending_staff: 'Awaiting you',
  resolved: 'Resolved',
  closed: 'Closed',
};

type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'compliance' | 'ai' | 'engine';

/** Badge tone per status — "awaiting you" and "open" read as needing action. */
export const STATUS_TONE: Record<TicketStatus, BadgeTone> = {
  open: 'warning',
  pending_staff: 'warning',
  pending_customer: 'compliance',
  resolved: 'success',
  closed: 'neutral',
};

export type TicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/** Legal explicit transitions the staff UI may offer FROM a given status.
 *  Mirrors SupportService::TRANSITIONS exactly (reply-driven ball moves are
 *  server-derived and are NOT offered as manual buttons). */
const TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  open: ['pending_customer', 'pending_staff', 'resolved', 'closed'],
  pending_customer: ['open', 'pending_staff', 'resolved', 'closed'],
  pending_staff: ['open', 'pending_customer', 'resolved', 'closed'],
  resolved: ['open', 'closed'],
  closed: ['open'],
};

export function nextStatuses(from: TicketStatus): TicketStatus[] {
  return TRANSITIONS[from] ?? [];
}

export function isTicketStatus(v: unknown): v is TicketStatus {
  return typeof v === 'string' && (TICKET_STATUSES as readonly string[]).includes(v);
}

export function isOpenState(s: TicketStatus): boolean {
  return s !== 'resolved' && s !== 'closed';
}

// ---- Unprovisioned detection ------------------------------------------------
/** The scaffold ships dark; the API returns this code until the DDL lands. */
export const UNPROVISIONED_CODE = 'support_unprovisioned';

/** True when an error (ApiError-shaped: { code }) is the dark-scaffold 503. The
 *  pages render this as a calm "not enabled yet" panel, never a red error. */
export function isUnprovisioned(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === UNPROVISIONED_CODE
  );
}

// ---- Wire shapes ------------------------------------------------------------
export interface TicketDto {
  id: number;
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  source: string;
  partner_ref: string;
  assignee_user: string;
  contact_id: number | null;
  first_response_at: string;
  last_message_at: string;
  resolved_at: string;
  closed_at: string;
  created_at: string;
  updated_at: string;
}

export interface MessageDto {
  id: number;
  ticket_id: number;
  author_kind: 'partner' | 'staff' | 'system';
  author_ref: string;
  body: string;
  is_internal: boolean;
  created_at: string;
}

export interface TicketListResponse {
  ok: boolean;
  tickets: TicketDto[];
}

export interface TicketDetailResponse {
  ok: boolean;
  ticket: TicketDto;
  messages: MessageDto[];
}

/** A safe view-row: the fields the inbox table renders, tone/label resolved. */
export interface TicketRow {
  id: number;
  subject: string;
  status: TicketStatus;
  statusLabel: string;
  statusTone: BadgeTone;
  priority: TicketPriority;
  priorityLabel: string;
  partnerRef: string;
  lastMessageAt: string;
}

export function toTicketRow(t: TicketDto): TicketRow {
  const status = isTicketStatus(t.status) ? t.status : 'open';
  const priority = (['low', 'normal', 'high', 'urgent'] as const).includes(t.priority)
    ? t.priority
    : 'normal';
  return {
    id: t.id,
    subject: t.subject || '(no subject)',
    status,
    statusLabel: STATUS_LABEL[status],
    statusTone: STATUS_TONE[status],
    priority,
    priorityLabel: PRIORITY_LABEL[priority],
    partnerRef: t.partner_ref || '—',
    lastMessageAt: t.last_message_at || t.created_at || '',
  };
}

/** Author label for a thread bubble. */
export function authorLabel(m: MessageDto): string {
  if (m.author_kind === 'staff') return m.is_internal ? 'You (internal note)' : 'You';
  if (m.author_kind === 'partner') return m.author_ref || 'Customer';
  return 'System';
}
